import { CollisionDetection, DragEndEvent, DragMoveEvent, DragStartEvent, Modifier, rectIntersection } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import * as monaco from 'monaco-editor';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/ReduxHooks';
import type {
  CodeSnippetCodeByDropZone,
  CodeSnippetDropZoneMode,
  CodeSnippetImport,
  CodeSnippetInsertionOutcome,
  CodeSnippetInsertionPayload,
  CodeSnippetPlacementAnalysis,
  CodeSnippetPlacementInvalidReason,
  CodeSnippetSupportingReceiver,
  dropInfo,
} from '../../Components/CodeEditor/types';
import {
  analyzeSnippetPlacement,
  combineSnippetSupportingCode,
  insertPreviewSnippet,
  insertSnippetAtLine,
  resolveSnippetCodeForTarget,
  resolveSnippetInsertText,
  uniquifyVariableName,
} from '../../Components/CodeEditor/utils/snippetInsertion';
import type { PendingActionDrop } from '../../Components/CodeEditor/components/ActionArgumentsDialog';
import type { ActionItem } from '../../redux/EditorSlice';
import { findActionDefForLabel } from '../../Components/CodeEditor/ConfigurationTabs/driveModeAutonomousActionUtils';
import type { PendingDeviceSnippetPortDrop } from '../../utils/deviceSnippetPortDrop';
import { parseDeviceSnippetPortDrop } from '../../utils/deviceSnippetPortDrop';
import { getInputArgNameForAxisAction } from '../../framework/framework-axis-actions';
import { useAllAvailableActions } from './useAllAvailableActions';
import { hasUnresolvedKeywordArgument } from '../../utils/pythonSourceScanUtils';

const FOLLOW_PATH_ACTION_CALL_RE = /\bfollow_path_action\s*\(/;
const EMPTY_CALL_RE = /\b[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*\(\s*\)/;

interface UseEditorDragDropParams {
  standaloneEditorRef: MutableRefObject<monaco.editor.IStandaloneCodeEditor | null>;
  previewModelRef: MutableRefObject<monaco.editor.ITextModel | null>;
  debouncedSync: (code: string) => void;
  onPathDropRequested: (code: string, targetLine: number, imports?: CodeSnippetImport[], snippetTitle?: string) => void;
  onNoPathsAvailable: () => void;
  onActionArgsDropRequested: (drop: PendingActionDrop) => void;
  onDeviceSnippetPortDropRequested: (drop: PendingDeviceSnippetPortDrop & { targetLine: number }) => void;
  onDuplicateDriveBaseDropRequested: () => void;
  onSnippetInserted?: (title: string) => void;
  onSnippetRejected?: (title: string, reason?: CodeSnippetPlacementInvalidReason) => void;
  onSnippetPlacementRequested?: (title: string, invalidReason?: CodeSnippetPlacementInvalidReason | null) => void;
  onSnippetPlacementInvalid?: (title: string, reason: CodeSnippetPlacementInvalidReason) => void;
  onSnippetPlacementCancelled?: (title: string) => void;
}

type SnippetDragEvent = DragStartEvent | DragMoveEvent | DragEndEvent;

function snippetPayloadFromEvent(event: SnippetDragEvent): CodeSnippetInsertionPayload {
  return {
    id: (event.active.data.current?.id as string | undefined) ?? String(event.active.id),
    title: (event.active.data.current?.title as string | undefined) ?? String(event.active.id),
    code: (event.active.data.current?.code as string | undefined) ?? '',
    insertText: event.active.data.current?.insertText as string | undefined,
    type: event.active.data.current?.type as string | undefined,
    dropZone: event.active.data.current?.dropZone as CodeSnippetDropZoneMode | undefined,
    codeByDropZone: event.active.data.current?.codeByDropZone as CodeSnippetCodeByDropZone | undefined,
    imports: event.active.data.current?.imports as CodeSnippetImport[] | undefined,
    receiverClassName: event.active.data.current?.receiverClassName as string | undefined,
    frameworkClassName: event.active.data.current?.frameworkClassName as string | undefined,
    supportingReceiver: event.active.data.current?.supportingReceiver as CodeSnippetSupportingReceiver | undefined,
  };
}

export function useEditorDragDrop({
  standaloneEditorRef,
  previewModelRef,
  debouncedSync,
  onPathDropRequested,
  onNoPathsAvailable,
  onActionArgsDropRequested,
  onDeviceSnippetPortDropRequested,
  onDuplicateDriveBaseDropRequested,
  onSnippetInserted,
  onSnippetRejected,
  onSnippetPlacementRequested,
  onSnippetPlacementInvalid,
  onSnippetPlacementCancelled,
}: UseEditorDragDropParams) {
  const project = useAppSelector((state) => state.project.loadedProject);
  const globalActionList = useAppSelector((state) => state.editor.actionList) as ActionItem[];
  const allAvailableActions = useAllAvailableActions();
  const globalAxisActionList = useAppSelector((state) => state.editor.axisActionList) as ActionItem[];

  const [dropInfo, setDropInfo] = useState<dropInfo>({ dropObject: { id: '', title: '', code: '' }, cursorPos: null });
  const [isDragging, setIsDragging] = useState(false);
  const [pendingSnippetPlacement, setPendingSnippetPlacement] = useState<CodeSnippetInsertionPayload | null>(null);
  const [pendingSnippetPlacementInvalidReason, setPendingSnippetPlacementInvalidReason] = useState<CodeSnippetPlacementInvalidReason | null>(null);
  const [draggingSnippetPlacementInvalidReason, setDraggingSnippetPlacementInvalidReason] = useState<CodeSnippetPlacementInvalidReason | null>(null);

  const draggingDecorationRef = useRef<monaco.editor.IEditorDecorationsCollection | null>();
  const lastPreviewRef = useRef<{
    line: number;
    numLines?: number;
    placementAnalysis?: CodeSnippetPlacementAnalysis;
  } | null>(null);
  const currentCursorPosition = useRef({ x: 0, y: 0 });
  const originalModelRef = useRef<monaco.editor.ITextModel | null>(null);
  const snippetPlacementDecorationRef = useRef<monaco.editor.IEditorDecorationsCollection | null>(null);
  const pendingSnippetPlacementRef = useRef<CodeSnippetInsertionPayload | null>(null);
  const pendingSnippetPlacementInvalidReasonRef = useRef<CodeSnippetPlacementInvalidReason | null>(null);
  const draggingSnippetPlacementInvalidReasonRef = useRef<CodeSnippetPlacementInvalidReason | null>(null);
  const confirmPendingSnippetPlacementRef = useRef<() => void>(() => undefined);

  const updatePendingSnippetPlacement = useCallback((payload: CodeSnippetInsertionPayload | null) => {
    pendingSnippetPlacementRef.current = payload;
    pendingSnippetPlacementInvalidReasonRef.current = null;
    setPendingSnippetPlacement(payload);
    setPendingSnippetPlacementInvalidReason(null);
  }, []);

  const updatePendingSnippetPlacementInvalidReason = useCallback((reason: CodeSnippetPlacementInvalidReason | null) => {
    const changed = pendingSnippetPlacementInvalidReasonRef.current !== reason;
    pendingSnippetPlacementInvalidReasonRef.current = reason;
    setPendingSnippetPlacementInvalidReason(reason);
    return changed;
  }, []);

  const updateDraggingSnippetPlacementInvalidReason = useCallback(
    (reason: CodeSnippetPlacementInvalidReason | null, title?: string) => {
      const changed = draggingSnippetPlacementInvalidReasonRef.current !== reason;
      draggingSnippetPlacementInvalidReasonRef.current = reason;
      setDraggingSnippetPlacementInvalidReason(reason);
      if (changed && reason && title) {
        onSnippetPlacementInvalid?.(title, reason);
      }
    },
    [onSnippetPlacementInvalid]
  );

  const clearSnippetPlacementDecoration = useCallback(() => {
    snippetPlacementDecorationRef.current?.clear();
    snippetPlacementDecorationRef.current = null;
  }, []);

  const clearPendingSnippetPlacement = useCallback(() => {
    clearSnippetPlacementDecoration();
    updatePendingSnippetPlacement(null);
  }, [clearSnippetPlacementDecoration, updatePendingSnippetPlacement]);

  const cancelSnippetPlacement = useCallback(() => {
    const pendingPlacement = pendingSnippetPlacementRef.current;
    if (!pendingPlacement) return;

    const origin = document.getElementById(pendingPlacement.id);
    clearPendingSnippetPlacement();
    if (origin instanceof HTMLElement) {
      origin.focus();
    } else {
      standaloneEditorRef.current?.focus();
    }
    onSnippetPlacementCancelled?.(pendingPlacement.title);
  }, [clearPendingSnippetPlacement, onSnippetPlacementCancelled, standaloneEditorRef]);

  const updateSnippetPlacementDecoration = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor, payload: CodeSnippetInsertionPayload) => {
      const model = editor.getModel();
      const targetLine = editor.getPosition()?.lineNumber;
      if (!model || !targetLine) {
        clearSnippetPlacementDecoration();
        return null;
      }

      const placement = analyzeSnippetPlacement(model, targetLine, payload.type, payload.dropZone, payload.code, payload.frameworkClassName);
      const decorations: monaco.editor.IModelDeltaDecoration[] = [
        {
          range: new monaco.Range(targetLine, 1, targetLine, 1),
          options: {
            isWholeLine: true,
            className: placement.valid ? 'snippet-placement-target' : 'snippet-placement-target-invalid',
          },
        },
      ];

      if (snippetPlacementDecorationRef.current) {
        snippetPlacementDecorationRef.current.set(decorations);
      } else {
        snippetPlacementDecorationRef.current = editor.createDecorationsCollection(decorations);
      }

      return placement;
    },
    [clearSnippetPlacementDecoration]
  );

  const setModelPreservingViewState = (editor: monaco.editor.IStandaloneCodeEditor, model: monaco.editor.ITextModel) => {
    if (editor.getModel() === model) return;
    const viewState = editor.saveViewState();
    try {
      editor.setModel(model);
      if (viewState) {
        editor.restoreViewState(viewState);
      }
    } catch {
      // Ignore swap errors
    }
  };

  const clearPreviewState = (editor?: monaco.editor.IStandaloneCodeEditor | null, restoreOriginalModel = false) => {
    if (restoreOriginalModel && editor && originalModelRef.current) {
      setModelPreservingViewState(editor, originalModelRef.current);
    }

    draggingDecorationRef.current?.clear();
    lastPreviewRef.current = null;
    previewModelRef.current?.dispose();
    previewModelRef.current = null;
    originalModelRef.current = null;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent | PointerEvent) => {
      currentCursorPosition.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('pointermove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('pointermove', handleMouseMove);
    };
  }, [isDragging, standaloneEditorRef]);

  const SCROLL_MARGIN = 80;
  const SCROLL_STEP = 16;
  const SCROLL_INTERVAL_MS = 50;

  useEffect(() => {
    if (!isDragging) return;

    const tick = () => {
      const editor = standaloneEditorRef.current;
      if (!editor) return;

      const { y: cursorY } = currentCursorPosition.current;
      const domNode = editor.getContainerDomNode();
      if (!domNode) return;

      const rect = domNode.getBoundingClientRect();
      if (cursorY < rect.top || cursorY > rect.bottom) return;
      const inTopZone = cursorY < rect.top + SCROLL_MARGIN;
      const inBottomZone = cursorY > rect.bottom - SCROLL_MARGIN;

      if (!inTopZone && !inBottomZone) return;

      const scrollTop = editor.getScrollTop();
      const scrollHeight = editor.getScrollHeight();
      const viewHeight = domNode.clientHeight;
      const maxScrollTop = Math.max(0, scrollHeight - viewHeight);

      if (inTopZone && scrollTop > 0) {
        editor.setScrollTop(Math.max(0, scrollTop - SCROLL_STEP));
      } else if (inBottomZone && scrollTop < maxScrollTop) {
        editor.setScrollTop(Math.min(maxScrollTop, scrollTop + SCROLL_STEP));
      }
    };

    const id = setInterval(tick, SCROLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isDragging, standaloneEditorRef]);

  const handleDragStart = (event: DragStartEvent) => {
    clearPendingSnippetPlacement();
    updateDraggingSnippetPlacementInvalidReason(null);
    const activatorCoordinates = getEventCoordinates(event.activatorEvent);
    if (activatorCoordinates) {
      currentCursorPosition.current = {
        x: activatorCoordinates.x,
        y: activatorCoordinates.y,
      };
    }

    clearPreviewState(standaloneEditorRef.current, false);
    setIsDragging(true);
    setDropInfo({
      dropObject: snippetPayloadFromEvent(event),
      cursorPos: null,
    });
  };

  const handleDragCancel = () => {
    document.body.style.cursor = 'default';
    setIsDragging(false);
    updateDraggingSnippetPlacementInvalidReason(null);
    clearPreviewState(standaloneEditorRef.current, true);
  };

  const handleDragging = (event: DragMoveEvent) => {
    const editor = standaloneEditorRef.current;
    if (!editor) return;

    if (event.over?.id !== 'editor') {
      document.body.style.cursor = 'not-allowed';
      updateDraggingSnippetPlacementInvalidReason(null);
      if (originalModelRef.current) setModelPreservingViewState(editor, originalModelRef.current);
    } else {
      document.body.style.cursor = 'copy';

      const { x, y } = currentCursorPosition.current;
      const mouseEvent = editor.getTargetAtClientPoint(x - 70, y);
      const cursorPos: monaco.Position | null | undefined = mouseEvent?.position;

      if (cursorPos) {
        const payload = snippetPayloadFromEvent(event);
        const currentModel = editor.getModel();
        if (!originalModelRef.current && currentModel) {
          originalModelRef.current = currentModel;
        }

        if (!previewModelRef.current && currentModel) {
          previewModelRef.current = monaco.editor.createModel(currentModel.getValue(), 'python');
        }

        if (!previewModelRef.current) return;

        if (editor.getModel() !== previewModelRef.current) {
          setModelPreservingViewState(editor, previewModelRef.current);
        }

        const modelForUniquify = originalModelRef.current ?? currentModel;
        let targetLineForResolution = cursorPos.lineNumber;
        const previousPreview = lastPreviewRef.current;
        if (previousPreview && editor.getModel() === previewModelRef.current && previousPreview.line < targetLineForResolution) {
          targetLineForResolution -= previousPreview.numLines ?? 1;
        }
        if (modelForUniquify) {
          targetLineForResolution = Math.min(Math.max(targetLineForResolution, 1), modelForUniquify.getLineCount());
        }

        const resolvedSnippet = resolveSnippetCodeForTarget({
          code: payload.code,
          codeByDropZone: payload.codeByDropZone,
          model: modelForUniquify,
          targetLine: targetLineForResolution,
          snippetType: payload.type,
          dropZone: payload.dropZone,
          receiverClassName: payload.receiverClassName,
          supportingReceiver: payload.supportingReceiver,
          frameworkClassName: payload.frameworkClassName,
          uniquifyTopLevelSetup: true,
        });
        insertPreviewSnippet({
          editor,
          textToInsert: combineSnippetSupportingCode(resolvedSnippet.code ?? '', resolvedSnippet.supportingCode),
          cursorPos,
          lastPreviewRef,
          draggingDecorationRef,
          snippetType: payload.type,
          dropZone: payload.dropZone,
          codeByDropZone: resolvedSnippet.codeByDropZone,
          frameworkClassName: payload.frameworkClassName,
        });
        const placement = lastPreviewRef.current?.placementAnalysis;
        updateDraggingSnippetPlacementInvalidReason(placement?.valid === false ? placement.reason : null, payload.title);
        document.body.style.cursor = placement?.valid === false ? 'not-allowed' : 'copy';
      }
    }
  };

  const insertSnippetAtTarget = ({
    editor,
    payload,
    targetLine,
    validationModel,
    placementAnalysis,
  }: {
    editor: monaco.editor.IStandaloneCodeEditor;
    payload: CodeSnippetInsertionPayload;
    targetLine: number;
    validationModel: monaco.editor.ITextModel | null;
    placementAnalysis?: CodeSnippetPlacementAnalysis | null;
  }): CodeSnippetInsertionOutcome => {
    const placement =
      placementAnalysis ??
      (validationModel
        ? analyzeSnippetPlacement(validationModel, targetLine, payload.type, payload.dropZone, payload.code, payload.frameworkClassName)
        : null);

    if (!placement?.valid) {
      if (placement?.reason === 'duplicate-drive-base') {
        onDuplicateDriveBaseDropRequested();
      }
      clearPreviewState(editor, true);
      onSnippetRejected?.(payload.title, placement?.reason);
      return 'rejected';
    }

    const resolvedSnippet = resolveSnippetCodeForTarget({
      code: payload.code,
      codeByDropZone: payload.codeByDropZone,
      model: validationModel,
      targetLine,
      snippetType: payload.type,
      dropZone: payload.dropZone,
      receiverClassName: payload.receiverClassName,
      supportingReceiver: payload.supportingReceiver,
      frameworkClassName: payload.frameworkClassName,
      resolvedDropZone: placement.resolvedDropZone,
    });
    const resolvedCode = resolvedSnippet.codeForTarget;
    const resolvedInsertText =
      payload.insertText && validationModel
        ? resolveSnippetInsertText(payload.insertText, validationModel, targetLine, payload.receiverClassName, payload.supportingReceiver)
        : undefined;

    const needsPath =
      typeof resolvedCode === 'string' &&
      FOLLOW_PATH_ACTION_CALL_RE.test(resolvedCode) &&
      (/\bpath\s*=\s*(?=[,)]|$)/.test(resolvedCode) || /\bname\s*=\s*(?=[,)]|$)/.test(resolvedCode));
    if (needsPath && resolvedCode) {
      clearPreviewState(editor, true);
      if ((project?.paths || []).length === 0) {
        onNoPathsAvailable();
        return 'dialog-requested';
      }
      onPathDropRequested(resolvedCode, targetLine, payload.imports, payload.title);
      return 'dialog-requested';
    }

    const devicePortParsed = resolvedCode ? parseDeviceSnippetPortDrop(resolvedCode, payload.type) : null;
    if (devicePortParsed) {
      clearPreviewState(editor, true);
      onDeviceSnippetPortDropRequested({
        ...devicePortParsed,
        snippetTitle: payload.title,
        targetLine,
        imports: payload.imports,
      });
      return 'dialog-requested';
    }

    if (typeof resolvedCode === 'string' && !FOLLOW_PATH_ACTION_CALL_RE.test(resolvedCode)) {
      const actionMatch = resolvedCode.match(/^([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)\s*\(/);
      const actionLabel = actionMatch?.[1] || '';
      const parsedActionDef = actionLabel ? findActionDefForLabel(globalActionList ?? [], actionLabel) : null;
      const ordinaryActionDef =
        parsedActionDef ??
        (actionLabel && hasUnresolvedKeywordArgument(resolvedCode) ? findActionDefForLabel(allAvailableActions, actionLabel) : null);
      const axisActionDef = ordinaryActionDef || !actionLabel ? null : findActionDefForLabel(globalAxisActionList ?? [], actionLabel);
      const actionDef = ordinaryActionDef ?? axisActionDef;
      const inputArgName = axisActionDef
        ? getInputArgNameForAxisAction(axisActionDef.name) ??
          axisActionDef.arguments?.find((argument) => String(argument.type ?? '').includes('Callable'))?.name
        : undefined;
      const hiddenArgumentNames = inputArgName ? [inputArgName] : [];
      const hasEditableArguments = (actionDef?.arguments ?? []).some((argument) => !hiddenArgumentNames.includes(argument.name));

      if (actionDef && hasEditableArguments) {
        clearPreviewState(editor, true);
        onActionArgsDropRequested({
          snippetTitle: payload.title,
          actionLabel,
          code: resolvedCode,
          insertText: resolvedInsertText,
          supportingCode: resolvedSnippet.supportingCode,
          targetLine,
          actionDef,
          imports: payload.imports,
          kind: axisActionDef ? 'input-mapping' : 'action',
          hiddenArgumentNames,
        });
        return 'dialog-requested';
      }
    }

    if (typeof resolvedCode === 'string' && hasUnresolvedKeywordArgument(resolvedCode)) {
      clearPreviewState(editor, true);
      onSnippetRejected?.(payload.title);
      return 'rejected';
    }

    const committedModel = originalModelRef.current ?? validationModel ?? editor.getModel();
    if (!resolvedCode || !committedModel) {
      clearPreviewState(editor, true);
      onSnippetRejected?.(payload.title);
      return 'rejected';
    }

    const commitResolvedSnippet = () => {
      const codeToInsert = uniquifyVariableName(resolvedCode, committedModel);
      const textToInsert = combineSnippetSupportingCode(codeToInsert, resolvedSnippet.supportingCode);
      const snippetTextToInsert = resolvedInsertText ? combineSnippetSupportingCode(resolvedInsertText, resolvedSnippet.supportingCode) : undefined;
      insertSnippetAtLine({
        editor,
        textToInsert,
        snippetTextToInsert,
        triggerParameterHints: Boolean(payload.insertText) || EMPTY_CALL_RE.test(codeToInsert),
        targetLine,
        imports: payload.imports,
      });
      const newCode = editor.getModel()?.getValue();
      if (newCode) debouncedSync(newCode);
      onSnippetInserted?.(payload.title);
    };

    if (payload.insertText && originalModelRef.current && typeof globalThis.requestAnimationFrame === 'function') {
      setModelPreservingViewState(editor, committedModel);
      clearPreviewState(editor, false);
      globalThis.requestAnimationFrame(commitResolvedSnippet);
      return 'inserted';
    }

    try {
      setModelPreservingViewState(editor, committedModel);
      commitResolvedSnippet();
    } finally {
      clearPreviewState(editor, false);
    }
    return 'inserted';
  };

  const requestSnippetPlacement = (payload: CodeSnippetInsertionPayload): CodeSnippetInsertionOutcome => {
    const editor = standaloneEditorRef.current;
    const validationModel = editor?.getModel() ?? null;
    if (!editor || !validationModel) {
      onSnippetRejected?.(payload.title);
      return 'rejected';
    }

    clearPendingSnippetPlacement();
    updatePendingSnippetPlacement(payload);
    const placement = updateSnippetPlacementDecoration(editor, payload);
    const invalidReason = placement?.valid === false ? placement.reason : null;
    updatePendingSnippetPlacementInvalidReason(invalidReason);
    editor.focus();
    onSnippetPlacementRequested?.(payload.title, invalidReason);
    return 'placement-requested';
  };

  const confirmPendingSnippetPlacement = () => {
    const payload = pendingSnippetPlacementRef.current;
    const editor = standaloneEditorRef.current;
    const validationModel = editor?.getModel() ?? null;
    const targetLine = editor?.getPosition()?.lineNumber;
    if (!payload) return;
    if (!editor || !validationModel || !targetLine) {
      clearPendingSnippetPlacement();
      onSnippetRejected?.(payload.title);
      editor?.focus();
      return;
    }

    const placement = analyzeSnippetPlacement(validationModel, targetLine, payload.type, payload.dropZone, payload.code, payload.frameworkClassName);
    if (!placement.valid && placement.reason === 'duplicate-drive-base') {
      insertSnippetAtTarget({ editor, payload, targetLine, validationModel, placementAnalysis: placement });
      clearPendingSnippetPlacement();
      return;
    }

    if (!placement.valid) {
      updatePendingSnippetPlacementInvalidReason(placement.reason);
      onSnippetPlacementInvalid?.(payload.title, placement.reason);
      editor.focus();
      return;
    }

    const outcome = insertSnippetAtTarget({
      editor,
      payload,
      targetLine,
      validationModel,
      placementAnalysis: placement,
    });
    if (outcome !== 'rejected') {
      clearPendingSnippetPlacement();
    } else {
      editor.focus();
    }
  };
  confirmPendingSnippetPlacementRef.current = confirmPendingSnippetPlacement;

  useEffect(() => {
    if (!pendingSnippetPlacement) return;

    const editor = standaloneEditorRef.current;
    if (!editor) return;

    const updatePlacementTarget = () => {
      const placement = updateSnippetPlacementDecoration(editor, pendingSnippetPlacement);
      const invalidReason = placement?.valid === false ? placement.reason : null;
      if (updatePendingSnippetPlacementInvalidReason(invalidReason)) {
        onSnippetPlacementRequested?.(pendingSnippetPlacement.title, invalidReason);
      }
    };
    updatePlacementTarget();
    const cursorListener = editor.onDidChangeCursorPosition(updatePlacementTarget);

    const handlePlacementKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancelSnippetPlacement();
        return;
      }

      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && standaloneEditorRef.current?.hasTextFocus()) {
        event.preventDefault();
        event.stopPropagation();
        confirmPendingSnippetPlacementRef.current();
      }
    };

    document.addEventListener('keydown', handlePlacementKeyDown, true);
    return () => {
      cursorListener.dispose();
      document.removeEventListener('keydown', handlePlacementKeyDown, true);
      clearSnippetPlacementDecoration();
    };
  }, [
    cancelSnippetPlacement,
    clearSnippetPlacementDecoration,
    onSnippetPlacementRequested,
    pendingSnippetPlacement,
    standaloneEditorRef,
    updatePendingSnippetPlacementInvalidReason,
    updateSnippetPlacementDecoration,
  ]);

  const handleDrop = (event: DragEndEvent): CodeSnippetInsertionOutcome | undefined => {
    document.body.style.cursor = 'default';
    setIsDragging(false);
    updateDraggingSnippetPlacementInvalidReason(null);
    draggingDecorationRef.current?.clear();
    const editor = standaloneEditorRef.current;

    if (!editor || event.over?.id !== 'editor') {
      clearPreviewState(editor, true);
      return;
    }

    const payload = snippetPayloadFromEvent(event);
    const getTargetLine = (): number => {
      if (lastPreviewRef.current?.line) return lastPreviewRef.current.line;
      const { x, y } = currentCursorPosition.current;
      const hit = editor.getTargetAtClientPoint(x - 70, y);
      return hit?.position?.lineNumber || editor.getPosition()?.lineNumber || 1;
    };

    const targetLine = getTargetLine();
    const validationModel = originalModelRef.current ?? editor.getModel();
    const placementAnalysis =
      lastPreviewRef.current?.placementAnalysis ??
      (validationModel
        ? analyzeSnippetPlacement(validationModel, targetLine, payload.type, payload.dropZone, payload.code, payload.frameworkClassName)
        : null);

    return insertSnippetAtTarget({
      editor,
      payload,
      targetLine,
      validationModel,
      placementAnalysis,
    });
  };

  const snapLeftToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
    if (draggingNodeRect && activatorEvent) {
      const activatorCoordinates = getEventCoordinates(activatorEvent);

      if (!activatorCoordinates) {
        return transform;
      }

      const offsetX = activatorCoordinates.x - draggingNodeRect.left;
      const offsetY = activatorCoordinates.y - draggingNodeRect.top;

      return {
        ...transform,
        x: transform.x + offsetX - 5,
        y: transform.y + offsetY - draggingNodeRect.height / 2,
      };
    }

    return transform;
  };

  const fixCursorSnapOffset: CollisionDetection = (args) => {
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      collisionRect: {
        width,
        height,
        bottom: y + height,
        left: x - width,
        right: x + width,
        top: y - height,
      },
    };
    return rectIntersection(updated);
  };

  return {
    isDragging,
    dropInfo,
    handleDragStart,
    handleDragging,
    handleDrop,
    handleDragCancel,
    requestSnippetPlacement,
    pendingSnippetPlacement,
    pendingSnippetPlacementInvalidReason,
    draggingSnippetPlacementInvalidReason,
    cancelSnippetPlacement,
    snapLeftToCursor,
    fixCursorSnapOffset,
  };
}
